import { useRef, useEffect, useMemo, useCallback, useState } from "react";
import {
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { VisibilityOff, Visibility } from "@mui/icons-material";
import ATextField from "../../../../components/UI/Inputs/MUI/ATextField/ATextField";
import Spinner from "../../../../components/UI/Spinner";
import styles from "./PasswordStep.module.scss";
import { useTranslation } from "react-i18next";

/**
 * Componente PasswordStep
 *
 * @description Componente responsável pelo passo de inserção de senha no fluxo de login.
 * Permite ao usuário inserir sua senha, alternar a visibilidade da senha, voltar para o
 * passo anterior ou acessar a funcionalidade de recuperação de senha.
 *
 * @param {Object} props - Propriedades do componente
 * @param {string} props.email - Email do usuário (apenas exibição)
 * @param {string} props.password - Valor atual da senha
 * @param {boolean} props.showPassword - Controla a visibilidade da senha
 * @param {string|null} props.errorMsg - Mensagem de erro, se houver
 * @param {boolean} props.isLoading - Indica se há processamento em andamento
 * @param {boolean} props.rememberMe - Estado do checkbox "Lembrar-me"
 * @param {Function} props.onChange - Função para atualizar os valores do estado
 * @param {Function} props.onBack - Função para voltar ao passo anterior
 * @param {Function} props.onForgotPassword - Função para iniciar o fluxo de recuperação de senha
 * @param {Function} props.onRememberMeChange - Função para lidar com mudanças no checkbox "Lembrar-me"
 */
const PasswordStep = ({
  email,
  password,
  showPassword,
  errorMsg,
  isLoading,
  rememberMe,
  onChange,
  onBack,
  onForgotPassword,
  onRememberMeChange,
}) => {
  // Referência para o campo de senha para foco automático
  const passwordInputRef = useRef(null);
  const passwordFormRef = useRef(null);

  // Tradução
  const { t } = useTranslation();

  // Estado local para controlar o checkbox de "Lembrar-me"
  const [localRememberMe, setLocalRememberMe] = useState(() => {
    // Verifica se o usuário já fez login antes
    const hasLoggedInBefore = localStorage.getItem("hasLoggedInBefore");

    if (hasLoggedInBefore) {
      // Se já fez login antes, use a preferência salva ou o valor padrão (false)
      const savedPreference = localStorage.getItem("rememberMePreference");
      return savedPreference === "true";
    }

    // Na primeira vez, sempre desmarcado
    return false;
  });

  // Efeito para sincronizar o estado local com as props
  useEffect(() => {
    // Atualiza o estado do componente pai com o valor local
    if (rememberMe !== localRememberMe) {
      onChange({ rememberMe: localRememberMe });
    }
  }, [localRememberMe, onChange, rememberMe]);

  /**
   * Efeito para focar automaticamente no campo de senha quando o componente é montado
   */
  useEffect(() => {
    if (passwordInputRef.current) {
      passwordInputRef.current.focus();

      // Tenta acionar o preenchimento automático
      if (!password) {
        // Técnica que pode ajudar a acionar o preenchimento automático
        setTimeout(() => {
          if (passwordInputRef.current) {
            // Simula interação com o campo para acionar o preenchimento automático
            passwordInputRef.current.click();
            passwordInputRef.current.focus();
          }
        }, 100);
      }
    }
  }, [password]);

  /**
   * Tenta acionar o preenchimento automático da senha ao montar o componente
   */
  useEffect(() => {
    // Tenta estimular o navegador a preencher a senha automaticamente
    if (passwordFormRef.current && !password) {
      const event = new Event("input", { bubbles: true });
      passwordFormRef.current.dispatchEvent(event);
    }
  }, [password]);

  /**
   * Alterna a visibilidade da senha
   * Memoizado com useCallback para evitar recriações desnecessárias
   */
  const togglePasswordVisibility = useCallback(() => {
    onChange({ showPassword: !showPassword });
  }, [onChange, showPassword]);

  /**
   * Manipula alterações no campo de senha e limpa mensagens de erro
   * Memoizado com useCallback para evitar recriações desnecessárias
   */
  const handlePasswordChange = useCallback(
    (e) => {
      onChange({
        password: e.target.value,
        errorMsg: null,
      });
    },
    [onChange]
  );

  /**
   * Manipula o clique no link "Esqueceu a senha"
   * Memoizado com useCallback para evitar recriações desnecessárias
   */
  const handleForgotPasswordClick = useCallback(
    (e) => {
      e.preventDefault();
      onForgotPassword();
    },
    [onForgotPassword]
  );

  /**
   * Manipula a mudança no checkbox "Lembrar-me"
   */
  const handleRememberMeChange = useCallback(
    (e) => {
      const newValue = e.target.checked;

      // Atualiza o estado local
      setLocalRememberMe(newValue);

      // Salva a preferência do usuário no localStorage
      localStorage.setItem("rememberMePreference", newValue.toString());

      // Marca que o usuário já fez login pelo menos uma vez
      localStorage.setItem("hasLoggedInBefore", "true");

      // Chama o callback original se existir
      if (onRememberMeChange) {
        onRememberMeChange(e);
      }
    },
    [onRememberMeChange]
  );

  /**
   * Determina se o botão de login deve estar desabilitado
   * Memoizado com useMemo para evitar recálculos desnecessários
   */
  const isLoginButtonDisabled = useMemo(
    () => isLoading || !password.trim(),
    [isLoading, password]
  );

  /**
   * Cria o ícone para o campo de senha com base no estado de visibilidade
   * Memoizado com useMemo para evitar recriações desnecessárias
   */
  const passwordEndAdornment = useMemo(
    () => (
      <InputAdornment position="end">
        <IconButton
          onClick={togglePasswordVisibility}
          edge="end"
          aria-label={showPassword ? "Esconder senha" : "Mostrar senha"}
          size="large"
          // Melhor segurança: prevenindo eventos de toque padrão em dispositivos móveis
          onMouseDown={(e) => e.preventDefault()}
          onTouchStart={(e) => e.preventDefault()}
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    ),
    [showPassword, togglePasswordVisibility]
  );

  return (
    <>
      {/* Div para ajudar com o preenchimento automático */}
      <div ref={passwordFormRef} style={{ display: "none" }}>
        <input
          type="text"
          name="fakeusernameremembered"
          style={{ display: "none" }}
        />
        <input
          type="password"
          name="fakepasswordremembered"
          style={{ display: "none" }}
        />
      </div>

      {/* Campo de email (desabilitado, apenas exibição) */}
      <ATextField
        value={email}
        label="E-mail"
        variant="outlined"
        disabled
        sx={{ 
          backgroundColor: "#F2F2F2","& .MuiInputLabel-root": {
            fontWeight: "500",
            fontSize: "1.1rem",
          }, 
          "& .MuiInputBase-input": {
            fontWeight: "500", 
            fontSize: "1rem",
          }
        }}
        aria-label="Email do usuário"
        id="email-display"
        name="email-display"
        InputProps={{
          readOnly: true,
        }}
      />

      {/* Campo de entrada da senha com toggle de visibilidade */}
      <ATextField
        inputRef={passwordInputRef}
        type={showPassword ? "text" : "password"}
        label="Digite sua senha"
        variant="outlined"
        required
        value={password}
        error={!!errorMsg}
        helperText={errorMsg}
        onChange={handlePasswordChange}
        sx={{ backgroundColor: "#F2F2F2", "& .MuiInputLabel-root": {
           fontWeight: "500",
           fontSize: "1.1rem",
         }, 
        }}
        aria-label="Campo de senha"
        id="password"
        name="password"
        autoComplete="current-password"
        InputProps={{
          endAdornment: passwordEndAdornment,
        }}
      />

      <div className={styles.auth_options}>
        {/* Checkbox "Lembrar minha senha" */}
        <FormControlLabel
          control={
            <Checkbox
              checked={localRememberMe}
              onChange={handleRememberMeChange}
              name="rememberMe"
              color="primary"
              id="rememberMe"
            />
          }
          label="Lembrar minha senha"
          className={styles.remember_me}
          sx={{
            width: "max-content",
            "& .MuiFormControlLabel-label": {
              fontWeight: 520,
            },
          }}
        />

        {/* Link para recuperação de senha */}
        <a
          href="/esqueci-senha"
          className={styles.forgot_password}
          onClick={handleForgotPasswordClick}
          aria-label="Esqueceu a senha"
        >
          {t("login_page.forgot_password")}
        </a>
      </div>

      {/* Botão de login com indicador de carregamento */}
      <button
        type="submit"
        className={styles.button_login}
        disabled={isLoginButtonDisabled}
        aria-label="Botão de login"
      >
        {isLoading ? <Spinner size="small" /> : "Entrar"}
      </button>

      {/* Botão para voltar ao passo anterior */}
      <button
        type="button"
        className={styles.back}
        onClick={onBack}
        disabled={isLoading}
        aria-label="Voltar para página anterior"
      >
        {t("login_page.exchange_email")}
      </button>
    </>
  );
};

export default PasswordStep;
